// No imports - everything will be lazy loaded

/**
 * Process image with UTIF.js as fallback when regular loading fails
 * @param {string} imageUrl - URL to the image
 * @returns {Promise<string>} - Blob URL of processed image
 */
export const processImageWithUTIF = async (imageUrl) => {
  try {
    // Lazy load UTIF
    const UTIF = (await import('utif')).default;
    
    // Fetch the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    
    // Try to parse with UTIF
    const ifds = UTIF.decode(arrayBuffer);
    
    if (!ifds || ifds.length === 0) {
      throw new Error('UTIF could not decode this image format');
    }
    
    // Use the first IFD (Image File Directory)
    const ifd = ifds[0];
    
    // Decode the image data
    UTIF.decodeImage(arrayBuffer, ifd, ifds);
    
    // Convert to RGBA
    const rgba = UTIF.toRGBA8(ifd);
    
    // Get orientation from EXIF data
    const orientation = ifd.t274 ? ifd.t274[0] : 1; // Default to 1 (no rotation)
    
    // Calculate canvas dimensions based on orientation
    let canvasWidth = ifd.width;
    let canvasHeight = ifd.height;
    
    // For orientations 5, 6, 7, 8 - swap width and height
    if (orientation >= 5 && orientation <= 8) {
      canvasWidth = ifd.height;
      canvasHeight = ifd.width;
    }
    
    // Create canvas with correct dimensions
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Apply orientation transformation
    ctx.save();
    
    switch (orientation) {
      case 2:
        // Flip horizontal
        ctx.translate(canvasWidth, 0);
        ctx.scale(-1, 1);
        break;
      case 3:
        // Rotate 180°
        ctx.translate(canvasWidth, canvasHeight);
        ctx.rotate(Math.PI);
        break;
      case 4:
        // Flip vertical
        ctx.translate(0, canvasHeight);
        ctx.scale(1, -1);
        break;
      case 5:
        // Rotate 90° CCW + flip horizontal
        ctx.rotate(-Math.PI / 2);
        ctx.translate(-ifd.width, 0);
        ctx.scale(-1, 1);
        break;
      case 6:
        // Rotate 90° CW
        ctx.rotate(Math.PI / 2);
        ctx.translate(0, -ifd.height);
        break;
      case 7:
        // Rotate 90° CW + flip horizontal
        ctx.rotate(Math.PI / 2);
        ctx.translate(0, -ifd.height);
        ctx.scale(-1, 1);
        break;
      case 8:
        // Rotate 90° CCW
        ctx.rotate(-Math.PI / 2);
        ctx.translate(-ifd.width, 0);
        break;
      default:
        // No rotation (orientation 1)
        break;
    }
    
    // Create and draw the image data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = ifd.width;
    tempCanvas.height = ifd.height;
    const tempCtx = tempCanvas.getContext('2d');
    
    const imageData = tempCtx.createImageData(ifd.width, ifd.height);
    imageData.data.set(rgba);
    tempCtx.putImageData(imageData, 0, 0);
    
    // Draw the temp canvas onto the main canvas with orientation applied
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.restore();
    
    // Convert canvas to blob URL
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/jpeg', 0.8);
    });
    
  } catch (error) {
    console.error('UTIF processing failed:', error);
    throw error;
  }
};

/**
 * Try both UTIF and HEIC processors and return the first one that succeeds
 * @param {string} imageUrl - URL to the image
 * @returns {Promise<string>} - Blob URL of processed image
 */
export const processImageWithFallback = async (imageUrl) => {
  try {
    // Lazy load the HEIC processor
    const { processImageWithHEIC } = await import('./heicProcessor');
    
    // Try both processors simultaneously
    const results = await Promise.allSettled([
      processImageWithUTIF(imageUrl),
      processImageWithHEIC(imageUrl)
    ]);
    
    // Find the first successful result
    const successful = results.find(result => result.status === 'fulfilled');
    
    if (successful) {
      return successful.value;
    }
    
    // If both failed, throw the first error
    const utifError = results[0].status === 'rejected' ? results[0].reason : null;
    const heicError = results[1].status === 'rejected' ? results[1].reason : null;
    
    throw new Error(`Both processors failed. UTIF: ${utifError?.message || 'Unknown error'}. HEIC: ${heicError?.message || 'Unknown error'}`);
    
  } catch (error) {
    console.error('Fallback image processing failed:', error);
    throw error;
  }
};